import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, AlertTriangle, UserCheck, ClipboardCheck, Pencil, Check, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const CHECKS = ["credential", "training", "background"];
const CHECK_LABEL = { credential: "Credential", training: "Training", background: "Background" };

function StatusPill({ status }) {
  return status === "ready"
    ? <span data-testid="status-ready" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full"><UserCheck className="w-3 h-3" /> Ready</span>
    : <span data-testid="status-action" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full"><AlertTriangle className="w-3 h-3" /> Action required</span>;
}

export default function WorkforceReadiness() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tax, setTax] = useState(null);
  const [caregivers, setCaregivers] = useState([]);
  const [records, setRecords] = useState([]);
  const [filterState, setFilterState] = useState("");
  const [check, setCheck] = useState({ caregiver_id: "", state: "" });
  const [checkResult, setCheckResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [add, setAdd] = useState({ caregiver_id: "", state: "", role: "caregiver" });

  const load = useCallback(() => {
    const q = filterState ? `?state=${filterState}` : "";
    api.get(`/workforce/readiness${q}`).then((r) => setRecords(r.data.records)).catch(() => {});
  }, [filterState]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/compliance/taxonomy").then((r) => setTax(r.data)).catch(() => {});
    api.get("/caregivers").then((r) => setCaregivers(r.data)).catch(() => {});
  }, [user, loading, navigate]);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const cgOptions = caregivers.length ? caregivers.map((c) => [c.id, c.name]) : [...new Map(records.map((r) => [r.caregiver_id, r.caregiver_name])).entries()];

  const runCheck = async () => {
    if (!check.caregiver_id || !check.state) return;
    const { data } = await api.post("/workforce/assignment-check", check);
    setCheckResult(data);
  };

  const toggleCheck = async (rec, key) => {
    const payload = { caregiver_id: rec.caregiver_id, state: rec.state, role: rec.role || "caregiver",
      credential: rec.checks.credential, training: rec.checks.training, background: rec.checks.background };
    payload[key] = !rec.checks[key];
    try { await api.put("/workforce/readiness", payload); load(); } catch { toast.error("Could not update"); }
  };

  const addRecord = async () => {
    if (!add.caregiver_id || !add.state) { toast.error("Pick caregiver and state"); return; }
    try {
      await api.put("/workforce/readiness", { ...add, credential: false, training: false, background: false });
      toast.success("Readiness record added"); setAdd({ caregiver_id: "", state: "", role: "caregiver" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add"); }
  };

  const removeRecord = async (rec) => {
    try { await api.delete(`/workforce/readiness/${rec.caregiver_id}/${rec.state}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-900 text-teal-400"><ShieldCheck className="w-6 h-6" /></span>
          <div><h1 className="font-display text-3xl font-bold text-blue-950">Workforce Readiness</h1><p className="text-slate-500">Per-state credential & training status before you assign a shift.</p></div>
        </div>
        <Button data-testid="edit-mode-toggle" onClick={() => setEditMode(!editMode)} variant={editMode ? "default" : "outline"} className={editMode ? "bg-teal-600 hover:bg-teal-700 text-white gap-2" : "gap-2 border-blue-900/20 text-blue-900"}>
          {editMode ? <><Check className="w-4 h-4" /> Done editing</> : <><Pencil className="w-4 h-4" /> Edit statuses</>}
        </Button>
      </div>

      {/* Assignment check widget */}
      <div className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm mb-6" data-testid="assignment-check">
        <h2 className="font-display font-bold text-blue-950 flex items-center gap-2 mb-4"><ClipboardCheck className="w-5 h-5 text-teal-600" /> Shift assignment check</h2>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-sm font-medium text-blue-950">Caregiver</label>
            <Select value={check.caregiver_id} onValueChange={(v) => setCheck({ ...check, caregiver_id: v })}>
              <SelectTrigger data-testid="check-caregiver" className="mt-1"><SelectValue placeholder="Select caregiver…" /></SelectTrigger>
              <SelectContent>{cgOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-blue-950">State</label>
            <Select value={check.state} onValueChange={(v) => setCheck({ ...check, state: v })}>
              <SelectTrigger data-testid="check-state" className="mt-1"><SelectValue placeholder="Select state…" /></SelectTrigger>
              <SelectContent className="max-h-72">{tax?.states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={runCheck} disabled={!check.caregiver_id || !check.state} data-testid="check-run" className="h-10 bg-teal-600 hover:bg-teal-700 text-white">Check</Button>
        </div>
        {checkResult && (
          <div className="mt-4 rounded-xl border border-blue-900/5 p-4 bg-slate-50" data-testid="check-result">
            <div className="flex items-center justify-between"><p className="text-sm font-medium text-blue-950">{checkResult.state_name}</p><StatusPill status={checkResult.status} /></div>
            <div className="mt-3 flex flex-wrap gap-2">
              {checkResult.checks.map((c) => (
                <span key={c.label} className={`text-xs px-2.5 py-1 rounded-full ${c.ok ? "bg-teal-50 text-teal-700" : "bg-red-50 text-red-600"}`}>{c.ok ? "✓" : "✕"} {c.label}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add record (edit mode) */}
      {editMode && (
        <div className="bg-white rounded-2xl p-5 border border-teal-500/30 shadow-sm mb-6" data-testid="add-readiness">
          <h3 className="font-display font-semibold text-blue-950 mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-teal-600" /> Add readiness record</h3>
          <div className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
            <Select value={add.caregiver_id} onValueChange={(v) => setAdd({ ...add, caregiver_id: v })}>
              <SelectTrigger data-testid="add-caregiver"><SelectValue placeholder="Caregiver" /></SelectTrigger>
              <SelectContent>{cgOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={add.state} onValueChange={(v) => setAdd({ ...add, state: v })}>
              <SelectTrigger data-testid="add-state"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent className="max-h-72">{tax?.states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={add.role} onValueChange={(v) => setAdd({ ...add, role: v })}>
              <SelectTrigger data-testid="add-role"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent className="max-h-72">{tax?.worker_roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={addRecord} data-testid="add-readiness-submit" className="h-10 bg-blue-900 hover:bg-blue-950 text-white">Add</Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <Select value={filterState || "all"} onValueChange={(v) => setFilterState(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="readiness-filter" className="w-56"><SelectValue placeholder="All states" /></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="all">All states</SelectItem>{tax?.states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{records.length} record{records.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="space-y-3" data-testid="readiness-list">
        {records.map((r) => (
          <div key={`${r.caregiver_id}-${r.state}`} data-testid={`readiness-${r.caregiver_id}-${r.state}`} className="bg-white rounded-xl border border-blue-900/5 p-4 flex items-center gap-4">
            {r.caregiver_image && <img src={r.caregiver_image} alt={r.caregiver_name} className="w-12 h-12 rounded-full object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-blue-950">{r.caregiver_name}</p>
              <p className="text-xs text-slate-500">{r.role_name} · {r.state_name}</p>
              {editMode ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {CHECKS.map((k) => (
                    <button key={k} data-testid={`toggle-${r.caregiver_id}-${r.state}-${k}`} onClick={() => toggleCheck(r, k)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${r.checks[k] ? "bg-teal-50 text-teal-700 border-teal-300" : "bg-red-50 text-red-600 border-red-200"}`}>
                      {r.checks[k] ? "✓" : "✕"} {CHECK_LABEL[k]}
                    </button>
                  ))}
                </div>
              ) : (
                r.missing.length > 0 && <p className="text-xs text-amber-700 mt-0.5">Missing: {r.missing.join(", ")}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusPill status={r.status} />
              {editMode && <button onClick={() => removeRecord(r)} data-testid={`remove-${r.caregiver_id}-${r.state}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
        {records.length === 0 && <div className="text-center py-16 bg-white rounded-2xl border border-blue-900/5"><p className="text-slate-500">No readiness records for this filter.</p></div>}
      </div>
    </div>
  );
}
